import React, {useEffect, useState} from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField
} from "@mui/material";
import {toast} from "react-toastify";
import {FlowProtectionRepository} from "../../repositories/FlowProtectionRepository";

const visuallyHidden = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0
};

/**
 * Set (first time) or update (rotate) a registry project's protection password.
 * mode="set" -> password + confirm password
 * mode="update" -> old password + new password + confirm new password
 */
export const FlowProtectionPasswordModal = ({open, onClose, projectId, mode = "set", onSuccess}) => {
  const [oldPassword, setOldPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const isUpdate = mode === "update";

  useEffect(() => {
    if (open) {
      setOldPassword("");
      setPassword("");
      setConfirmPassword("");
      setError("");
      setLoading(false);
    }
  }, [open]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (loading || !projectId) return;
    if (!password || !confirmPassword || (isUpdate && !oldPassword)) return;
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    setError("");
    const request = isUpdate
      ? FlowProtectionRepository.updatePassword({projectId, oldPassword, newPassword: password})
      : FlowProtectionRepository.setPassword({projectId, password});

    request
      .then(() => {
        setLoading(false);
        toast.success(isUpdate ? "Protection password updated" : "Protection password set");
        onSuccess?.();
        onClose?.();
      })
      .catch((err) => {
        setLoading(false);
        const status = err?.response?.status;
        const message = err?.response?.data?.message || (
          isUpdate && status === 401 ? "Current password is incorrect." :
            isUpdate && status === 404 ? "No password configured yet for this project." :
              !isUpdate && status === 409 ? "A password is already configured for this project." :
                "Failed to save protection password."
        );
        setError(message);
      });
  };

  const canSubmit = !!password && !!confirmPassword && (!isUpdate || !!oldPassword) && !loading;

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{isUpdate ? "Update Protection Password" : "Set Protection Password"}</DialogTitle>
      <Box component={"form"} onSubmit={handleSubmit}>
        <DialogContent>
          <Stack gap={2} sx={{pt: 1}}>
            <DialogContentText>
              {isUpdate
                ? "This password gates access to protected user messages for this project. Enter your current password and choose a new one."
                : "Set a password that gates access to protected user messages for this project."}
            </DialogContentText>
            <input
              type={"text"}
              name={"flow-protection-username"}
              autoComplete={"username"}
              value={String(projectId || "")}
              readOnly
              style={visuallyHidden}
              tabIndex={-1}
              aria-hidden={"true"}
            />
            {isUpdate &&
              <TextField
                autoFocus
                type={"password"}
                label={"Current Password"}
                name={"flow-protection-old-password"}
                autoComplete={"current-password"}
                value={oldPassword}
                onChange={(e) => {
                  setOldPassword(e.target.value);
                  setError("");
                }}
                fullWidth
              />
            }
            <TextField
              autoFocus={!isUpdate}
              type={"password"}
              label={isUpdate ? "New Password" : "Password"}
              name={"flow-protection-new-password"}
              autoComplete={"new-password"}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError("");
              }}
              fullWidth
            />
            <TextField
              type={"password"}
              label={"Confirm Password"}
              name={"flow-protection-confirm-password"}
              autoComplete={"new-password"}
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setError("");
              }}
              error={!!error}
              helperText={error || " "}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{px: 3, pb: 3}}>
          <Button variant={"ghost"} onClick={onClose} disabled={loading}>Cancel</Button>
          <Button type={"submit"} variant={"primary"} disabled={!canSubmit}>
            {loading ? "Saving..." : isUpdate ? "Update Password" : "Set Password"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
